import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { ContractKeys } from '../../pages/TopdhaContractManagement/contracts/constants/contract.tableConfig';

export enum ContractTableConfigPage {
  LISTING = 'LISTING',
  REPORTS = 'REPORTS',
  APPROVALS = 'APPROVALS',
  PNL_REPORTS = 'PNL_REPORTS',
}

export interface ColumnConfigItem {
  label: string;
  key: ContractKeys;
  order: number;
}

export interface FieldConfigDto {
  data: ColumnConfigItem[];
}

export interface CreateTableConfigDto {
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault?: boolean;
}

export interface UpdateTableConfigDto {
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault?: boolean;
}

export interface ApplyTableConfigDto {
  configId: number;
  page: ContractTableConfigPage;
}

export interface ContractTableConfig {
  id: number;
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault: boolean;
  page?: ContractTableConfigPage;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

const contractTableConfigApi = createApi({
  reducerPath: 'contractTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ContractTableConfig'],
  endpoints: builder => ({
    getContractTableConfigList: builder.query<
      BackendResponse<ContractTableConfig[]>,
      { page: ContractTableConfigPage }
    >({
      query: params => ({
        url: `/contract-table-config/contract-config-list${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ContractTableConfig'],
    }),
    getContractTableConfig: builder.query<
      BackendResponse<ContractTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/contract-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'ContractTableConfig', id },
      ],
    }),
    getCurrentContractTableConfig: builder.query<
      BackendResponse<ContractTableConfig>,
      { page: ContractTableConfigPage }
    >({
      query: params => ({
        url: `/contract-table-config/current-config-page${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['ContractTableConfig'],
    }),
    createContractTableConfig: builder.mutation<
      BackendResponse<ContractTableConfig>,
      CreateTableConfigDto & { page: ContractTableConfigPage }
    >({
      query: payload => ({
        url: '/contract-table-config/create-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ContractTableConfig'],
    }),
    updateContractTableConfig: builder.mutation<
      BackendResponse<ContractTableConfig>,
      { id: number; data: UpdateTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/contract-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ContractTableConfig'],
    }),
    deleteContractTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/contract-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ContractTableConfig'],
    }),
    applyContractTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplyTableConfigDto
    >({
      query: payload => ({
        url: '/contract-table-config/apply-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['ContractTableConfig'],
    }),
  }),
});

export const {
  useGetContractTableConfigListQuery,
  useGetContractTableConfigQuery,
  useGetCurrentContractTableConfigQuery,
  useCreateContractTableConfigMutation,
  useUpdateContractTableConfigMutation,
  useDeleteContractTableConfigMutation,
  useApplyContractTableConfigMutation,
} = contractTableConfigApi;

export default contractTableConfigApi;
